import React, { useMemo, useState } from "react";
import { ShieldCheck, ShieldAlert, ShieldQuestion, Music, Maximize2 } from "lucide-react";
import {
  generateFakeFilename, pickFakeBitrate, calcFakeSize, formatSize,
  formatDuration, fakePeerCount, fakeSpeed, fakeScanStatus,
} from "@/lib/fakeData";

const ESSENTIAL_HEADERS = [
  { key: "idx",    label: "#",        w: 44 },
  { key: "file",   label: "Filename", grow: true, minW: 240 },
  { key: "artist", label: "Artist",   w: 108 },
  { key: "album",  label: "Album",    w: 104 },
  { key: "len",    label: "Length",   w: 54, align: "right" },
  { key: "scan",   label: "Status",   w: 96, align: "center" },
];

export default function ResultsTable({
  tracks, loading, onSelect, selectedId, queueIds = [],
  onExpand,
  expanded = false,
}) {
  const [focused, setFocused] = useState(null);

  const rows = useMemo(() => tracks.map((t) => {
    const br = pickFakeBitrate(t);
    return {
      track: t,
      filename: generateFakeFilename(t),
      bitrate: br,
      size: calcFakeSize(t, br),
      peers: fakePeerCount(t),
      speed: fakeSpeed(t),
      scanStatus: fakeScanStatus(t),
    };
  }), [tracks]);

  return (
    <div data-testid="results-table" className="flex flex-col flex-1 bevel-in bg-white min-h-0">
      <div className="flex items-center gap-2 px-2 py-1 border-b border-[#9C9A7E] bg-[#ECE9D8] text-[12px]">
        <Music size={12} className="text-[#003c9e]" />
        <span className="font-bold">
          {loading ? "Searching..." : `${rows.length} file${rows.length === 1 ? "" : "s"} found`}
        </span>
        {!loading && rows.length > 0 && (
          <span className="text-[#555]">· 0.42 sec · {rows.filter(r => r.scanStatus === "clean").length} clean</span>
        )}
        {!expanded && onExpand && rows.length > 0 && (
          <button
            data-testid="results-expand"
            onClick={onExpand}
            className="ml-auto bevel-btn px-3 py-[3px] text-[11px] flex items-center gap-1 font-bold"
            title="Expand to full-window view"
          >
            <Maximize2 size={11} />
            Expand
          </button>
        )}
      </div>

      <div className="flex bg-[#ECE9D8] border-b border-[#808080] select-none text-[12px] font-bold">
        {ESSENTIAL_HEADERS.map((h) => (
          <HdCell key={h.key} w={h.w} grow={h.grow} minW={h.minW} align={h.align}>{h.label}</HdCell>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden xp-scroll min-h-0">
        {loading && (
          <div className="p-6 text-center text-[13px] text-[#333] flex flex-col items-center gap-3">
            <div className="boot-progress"><div className="boot-progress-fill" /></div>
            <div>Crawling the peer swarm...</div>
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="p-8 text-center text-[13px] text-[#555] flex flex-col items-center gap-2">
            <Music size={32} className="text-[#999]" />
            <div>No results. Try "Evanescence", "Linkin Park", or "Britney Spears".</div>
            <div className="text-[11px] text-[#888]">Filtered to early-2000s peer-swarm era.</div>
          </div>
        )}

        {!loading && rows.map((r, i) => {
          const inQueue = queueIds.includes(r.track.id);
          const selected = selectedId === r.track.id || focused === r.track.id;
          const rowClass = `flex cursor-pointer border-b border-[#F0EFE7] ${selected ? "row-selected" : i % 2 === 0 ? "bg-white" : "bg-[#F5F3E8]"} hover:bg-[#D0E4FB]`;
          return (
            <div
              key={r.track.id}
              data-testid={`result-row-${i}`}
              onClick={() => {
                setFocused(r.track.id);
                onSelect(r.track);
              }}
              className={rowClass}
            >
              <Cell w={44}>
                <span className="text-[12px] font-mono">{i + 1}</span>
                {inQueue && (
                  <span
                    className="q-indicator ml-1 inline-flex items-center justify-center w-[14px] h-[14px] rounded-sm font-bold text-[9px]"
                    style={{
                      background: "#003c9e",
                      color: "#FFEB3B",
                      border: "1px solid #001A4D",
                      lineHeight: 1,
                    }}
                    title="In your queue"
                  >
                    Q
                  </span>
                )}
              </Cell>
              <Cell grow minW={240} className="min-w-0">
                <span
                  className="mono-filename text-[#003c9e] truncate block text-[13px]"
                  style={{ fontFamily: '"Share Tech Mono", monospace' }}
                  title={r.filename}
                >
                  {r.filename}
                </span>
              </Cell>
              <Cell w={108} className="truncate text-[12px]">{r.track.artist}</Cell>
              <Cell w={104} className="truncate text-[12px] text-[#555]">{r.track.album || "—"}</Cell>
              <Cell w={54} align="right" className="font-mono text-[12px]">{formatDuration(r.track.duration_ms)}</Cell>
              <Cell w={96} align="center">
                <ScanBadge status={r.scanStatus} />
              </Cell>
              {expanded && (
                <>
                  <Cell w={72} align="right" className="text-[12px]">{formatSize(r.size)}</Cell>
                  <Cell w={50} align="right" className="font-mono text-[12px]">{r.bitrate}</Cell>
                  <Cell w={56} align="right" className="font-mono text-[12px]">{r.peers}</Cell>
                  <Cell w={76} align="right" className="font-mono text-[12px] text-[#1f8020]">{r.speed} KB/s</Cell>
                </>
              )}
            </div>
          );
        })}
      </div>

      {!loading && rows.length > 0 && (
        <div className="px-2 py-1 text-[11px] bg-[#ECE9D8] border-t border-[#9C9A7E] text-[#555] flex justify-between">
          <span>Tip: Click any file to scan &amp; play. Keyboard: Space · ← · →</span>
          <span>{rows.length} hit{rows.length === 1 ? "" : "s"} in the swarm</span>
        </div>
      )}
    </div>
  );
}

function ScanBadge({ status }) {
  if (status === "suspicious") {
    return (
      <span
        className="scan-badge inline-flex items-center gap-1 px-1.5 py-[2px] rounded-[2px] text-[10px] font-bold"
        style={{
          background: "linear-gradient(to bottom, #F58D85, #E81123, #8B0A14)",
          color: "white",
          border: "1px solid #5B0207",
          textShadow: "1px 1px 1px rgba(0,0,0,0.4)",
          whiteSpace: "nowrap",
        }}
        title="Flagged as SUSPICIOUS by LemonShield. Double-click to scan again."
      >
        <ShieldAlert size={11} />
        SUS
      </span>
    );
  }
  if (status === "unverified") {
    return (
      <span
        className="scan-badge inline-flex items-center gap-1 px-1.5 py-[2px] rounded-[2px] text-[10px] font-bold"
        style={{
          background: "linear-gradient(to bottom, #FFF59D, #FFEB3B, #FBC02D)",
          color: "#3D2E00",
          border: "1px solid #9C7800",
          textShadow: "1px 1px 0 rgba(255,255,255,0.5)",
          whiteSpace: "nowrap",
        }}
        title="Not yet scanned. Double-click to run LemonShield."
      >
        <ShieldQuestion size={11} />
        UNVER
      </span>
    );
  }
  return (
    <span
      className="scan-badge inline-flex items-center gap-1 px-1.5 py-[2px] rounded-[2px] text-[10px] font-bold"
      style={{
        background: "linear-gradient(to bottom, #90EE90, #32CD32, #1F8020)",
        color: "white",
        border: "1px solid #0F4F0F",
        textShadow: "1px 1px 1px rgba(0,0,0,0.3)",
        whiteSpace: "nowrap",
      }}
      title="100% clean — verified by LemonShield."
    >
      <ShieldCheck size={11} />
      CLEAN
    </span>
  );
}

function HdCell({ children, w, grow, minW, align }) {
  return (
    <div
      className="px-2 py-[5px] border-r border-[#9C9A7E] truncate"
      style={{
        width: w,
        flex: grow ? 1 : undefined,
        minWidth: minW || (grow ? 0 : undefined),
        textAlign: align || "left",
      }}
    >
      {children}
    </div>
  );
}

function Cell({ children, w, grow, minW, align, className = "" }) {
  return (
    <div
      className={`px-2 py-[4px] border-r border-[#F0EFE7] flex items-center gap-1 ${className}`}
      style={{
        width: w,
        flex: grow ? 1 : undefined,
        minWidth: minW,
        justifyContent: align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start",
      }}
    >
      {children}
    </div>
  );
}
